import {Component, computed, effect, ElementRef, inject, signal, Type, viewChild, WritableSignal} from '@angular/core';
import {NgOptimizedImage, NgTemplateOutlet} from '@angular/common';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {MatDialogRef} from '@angular/material/dialog';
import {TranslatePipe} from '@ngx-translate/core';
import {OverlayscrollbarsModule} from 'overlayscrollbars-ngx';
import {PANEL_SCROLL_OPTIONS} from 'src/app/utils/scrollbars.utils';

import {GlobalResearchStore} from './global-research.store';
import {SearchEntityType, SearchItem} from 'src/app/services/search/global-research.model';
import {DataCollectionStatus, parseDataCollectionStatus} from 'src/app/services/servers/server.model';
import {RightSliderService} from '../../../services/ui/right-slider.service';
import {CurrentTenantStore} from 'src/app/services/tenant/current-tenant.store';
import {LayoutStore} from 'src/app/services/ui/layout.store';
import {MatMenu, MatMenuTrigger} from "@angular/material/menu";
import {DashboardPageLoaderComponent} from "../../global/title-content-loader/dashboard-page-loader.component";
import {ErrorStateComponent} from 'src/app/modules/design-system/error-state/error-state.component';
import {DisplayLogoComponent} from 'src/app/modules/design-system/display-logo/display-logo.component';
import {SpinnerComponent} from 'src/app/modules/design-system/spinner/spinner.component';

/**
 * Global search palette, opened anchored near the top of the screen by
 * GlobalResearchShortcutService (Cmd+K / Ctrl+K).
 * Selecting a result closes the palette and opens the matching detail drawer in the right slider.
 */
@Component({
	selector: 'app-global-research',
	imports: [NgOptimizedImage, NgTemplateOutlet, ReactiveFormsModule, TranslatePipe, DisplayLogoComponent, SpinnerComponent, OverlayscrollbarsModule, MatMenu, MatMenuTrigger, DashboardPageLoaderComponent, ErrorStateComponent],
	providers: [GlobalResearchStore],
	templateUrl: './global-research.component.html',
	styleUrl: './global-research.component.scss',
})
export class GlobalResearchComponent {
	readonly store = inject(GlobalResearchStore);
	private rightSliderService = inject(RightSliderService);
	private tenant = inject(CurrentTenantStore);
	protected readonly layout = inject(LayoutStore);
	private dialogRef: MatDialogRef<GlobalResearchComponent, SearchItem> = inject(MatDialogRef);

	readonly searchControl = new FormControl('', {nonNullable: true});
	protected readonly SearchEntityType = SearchEntityType;
	protected readonly DataCollectionStatus = DataCollectionStatus;

	readonly icons = {
		search: 'assets/icons/global-research/search.svg',
		clear: 'assets/icons/global-research/clear.svg',
		all: 'assets/icons/global-research/all.svg',
		seeMore: 'assets/icons/global-research/see-more.svg',
		arrowRight: 'assets/icons/global-research/arrow-right.svg',
	};

	readonly resultsScrollOptions = PANEL_SCROLL_OPTIONS;

	private readonly chromeRef = viewChild<ElementRef<HTMLElement>>('chrome');
	private readonly contentRef = viewChild<ElementRef<HTMLElement>>('measuredContent');
	private readonly chromeHeight = signal(0);
	private readonly contentHeight = signal(0);
	// Téléphone : le panneau est plein écran (hauteur du dialog), pas de hauteur mesurée.
	readonly boxHeight = computed(() => this.layout.isHandset() ? null : this.chromeHeight() + this.contentHeight());

	constructor() {
		this.store.search(this.searchControl.valueChanges);
		this.store.loadSuggestions();

		this.observeHeight(this.chromeRef, this.chromeHeight);
		this.observeHeight(this.contentRef, this.contentHeight);
	}

	/** Re-attaches a ResizeObserver to `ref()`'s element whenever it changes (e.g. a branch swap swaps in a new content element), writing its live height to `target`. */
	private observeHeight(ref: () => ElementRef<HTMLElement> | undefined, target: WritableSignal<number>): void {
		effect((onCleanup) => {
			const el = ref()?.nativeElement;
			if (!el) {
				target.set(0);
				return;
			}
			// `offsetHeight` (border-box, incl. padding) — not `entry.contentRect` (content-box
			// only), which under-reports by the element's own padding (e.g. `.results`'s
			// `padding-S`) and made the box a few px short of its real content.
			const observer = new ResizeObserver(() => target.set(el.offsetHeight));
			observer.observe(el);
			onCleanup(() => observer.disconnect());
		});
	}

	clearSearch(): void {
		this.searchControl.setValue('');
	}

	/** Chip icon of a type — the copies are named after the SearchEntityType, lowercase. */
	filterIcon(filter: SearchEntityType): string {
		return `assets/icons/global-research/${filter.toLowerCase()}.svg`;
	}

	/** Result-row icon — named after the backend `icon` value (server type, "network", "laptop-02"…). */
	itemIcon(item: SearchItem): string {
		const folder = item.type === SearchEntityType.SERVER ? 'servers' : 'rows';
		return `assets/icons/global-research/${folder}/${item.icon}.svg`;
	}

	/** The API may send the status in any case — normalize it before comparing to the enum. */
	serverStatus(item: SearchItem): DataCollectionStatus | undefined {
		return parseDataCollectionStatus(item.serverDataCollectionStatus);
	}

	close(): void {
		this.dialogRef.close();
	}

	select(item: SearchItem): void {
		this.dialogRef.close(item);

		// The drawers load on demand: the search lives in the shell's first bundle, they don't have to.
		switch (item.type) {
			case SearchEntityType.APPLICATION:
				this.openDrawer(import('../../home/applications/application-detail/application-detail.component').then(m => m.ApplicationDetailComponent), {applicationId: item.id});
				break;
			case SearchEntityType.SERVER: {
				const tenantId = this.tenant.currentTenantId();
				if (tenantId) {
					this.openDrawer(import('../../home/server/server-detail/server-detail.component').then(m => m.ServerDetailComponent), {tenantId, serverId: item.id});
				}
				break;
			}
			case SearchEntityType.NETWORK:
				this.openDrawer(import('../../home/network/network-detail/network-detail.component').then(m => m.NetworkDetailComponent), {networkId: item.id});
				break;
			case SearchEntityType.ROUTER:
				this.openDrawer(import('../../home/network/router-detail/router-detail.component').then(m => m.RouterDetailComponent), {routerId: item.id});
				break;
			case SearchEntityType.WORKSTATION:
				this.openDrawer(import('../../home/workstation/workstation-detail/workstation-detail.component').then(m => m.WorkstationDetailComponent), {workstationId: item.id});
				break;
			case SearchEntityType.MOBILE_DEVICE:
				this.openDrawer(import('../../home/devices/mobile-device-detail/mobile-device-detail.component').then(m => m.MobileDeviceDetailComponent), {mobileDeviceId: item.id});
				break;
			case SearchEntityType.DATA:
				this.openDrawer(import('../../home/data/data-detail/data-detail.component').then(m => m.DataDetailComponent), {dataId: item.id});
				break;
			case SearchEntityType.USER:
				this.openDrawer(import('../../home/user/user-detail/user-detail.component').then(m => m.UserDetailComponent), {userId: item.id});
				break;
			case SearchEntityType.ORGANIZATION:
				this.openDrawer(import('../../home/organization/organization-detail/organization-detail.component').then(m => m.OrganizationDetailComponent), {organizationId: item.id});
				break;
		}
	}

	private openDrawer(drawer: Promise<Type<unknown>>, data: unknown): void {
		drawer.then(component => this.rightSliderService.openComponent(component, data));
	}
}
